import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Edit3,
    Trash2,
    FileText,
    ExternalLink,
    X,
    AlertCircle,
    Code
} from 'lucide-react';
import './PrintableManager.css';
import api from '../api/axiosConfig';

const PrintableManager = () => {
    const [printables, setPrintables] = useState([]);
    const [metaOptions, setMetaOptions] = useState({ grades: [], subjects: []});
    const [loading, setLoading] = useState(true);

    // Search & Table Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [filterSubjectId, setFilterSubjectId] = useState('ALL');
    const [filterGradeId, setFilterGradeId] = useState('ALL');

    // Modal & Form State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [jsonError, setJsonError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        gradeId: '',
        subjectId: '',
        pdfUrl: '',
        thumbnailUrl: '',
        templateKey: '',
        metadataString: '',
        isFree: true,
    });

    // Load initial dataset
    useEffect(() => {
        fetchData();
    }, []);


    const fetchData = async () => {
        setLoading(true);
        try {
            const [printablesRes, optionsRes] = await Promise.all([
                api.get('/admin/printables'),
                api.get('/admin/printables/meta-options'),
            ]);

            const printablesData = await printablesRes.data;
            const optionsData = await optionsRes.data;
            setPrintables(printablesData);
            setMetaOptions(optionsData);
        } catch (err) {
            console.error('Error fetching admin printables:', err);
        } finally {
            setLoading(false);
        }
    };

    // Open Modal for Add or Edit
    const handleOpenModal = (item = null) => {
        setJsonError('');
        if (item) {
            setEditingItem(item);
            setFormData({
                title: item.title || '',
                description: item.description || '',
                gradeId: item.gradeId || '',
                subjectId: item.subjectId || '',
                pdfUrl: item.pdfUrl || '',
                thumbnailUrl: item.thumbnailUrl || '',
                templateKey: item.templateKey || '',
                metadataString: item.metadata ? JSON.stringify(item.metadata, null, 2) : '',
                isFree: item.isFree ?? true,
            });
        } else {
            setEditingItem(null);
            setFormData({
                title: '',
                description: '',
                gradeId: '',
                subjectId: '',
                pdfUrl: '',
                thumbnailUrl: '',
                templateKey: '',
                metadataString: '',
                isFree: true,
            });
        }
        setIsModalOpen(true);
    };

    // Prettify JSON in Metadata textarea
    const handlePrettifyJson = () => {
        if (!formData.metadataString.trim()) return;
        try {
            const parsed = JSON.parse(formData.metadataString);
            setFormData({ ...formData, metadataString: JSON.stringify(parsed, null, 2) });
            setJsonError('');
        } catch (err) {
            setJsonError('Invalid JSON format');
        }
    };

    // Save / Update Record
    const handleSubmit = async (e) => {
        e.preventDefault();
        setJsonError('');

        let parsedMetadata = null;
        if (formData.metadataString.trim()) {
            try {
                parsedMetadata = JSON.parse(formData.metadataString);
            } catch (err) {
                setJsonError('Invalid JSON format. Check syntax and quotes.');
                return;
            }
        }

        setSubmitting(true);

        const payload = {
            title: formData.title,
            description: formData.description,
            gradeId: formData.gradeId ? Number(formData.gradeId) : null,
            subjectId: formData.subjectId ? Number(formData.subjectId) : null,
            pdfUrl: formData.pdfUrl || null,
            thumbnailUrl: formData.thumbnailUrl || null,
            templateKey: formData.templateKey || null,
            metadata: parsedMetadata,
            isFree: formData.isFree,
        };

        try {
            try {
                const url = editingItem ? `/admin/printables/${editingItem.id}` : '/admin/printables';

                // Use api.put or api.post directly with payload as a JS object
                const res = editingItem
                    ? await api.put(url, payload)
                    : await api.post(url, payload);

                setIsModalOpen(false);
                fetchData();
            } catch (err) {
                console.error('Error saving printable:', err);
                alert(`Failed to save: ${err.response?.data?.error || err.message}`);
            }
        } catch (err) {
            alert('Network error while saving printable record.');
        } finally {
            setIsModalOpen(false);
            setSubmitting(false);
        }
    };

    // Delete Record
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this printable resource?')) return;

        try {
            const res = await api.delete(`/admin/printables/${id}`);
            if (res.ok) {
                fetchData();
            } else {
                alert('Failed to delete printable.');
            }
        } catch (err) {
            alert('Network error while deleting.');
        }
    };

    const formSubjects = formData.gradeId
        ? metaOptions.subjects.filter((s) => String(s.gradeId) === String(formData.gradeId))
        : metaOptions.subjects;

    // Filter Table Records
    const filteredPrintables = printables.filter((p) => {
        const matchesSearch =
            p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesSubject = filterSubjectId === 'ALL' || p.subjectId === Number(filterSubjectId);
        const matchesGrade = filterGradeId === 'ALL' || p.gradeId === Number(filterGradeId);

        return matchesSearch && matchesSubject && matchesGrade;
    });

    return (
        <div className="printable-manager-container">
            {/* Top Header */}
            <div className="manager-header">
                <div>
                    <h2>Printables Library</h2>
                    <p>Manage static PDF worksheets and dynamic SVG learning resources for students.</p>
                </div>
                <button className="create-printable-btn" onClick={() => handleOpenModal()}>
                    <Plus size={18} />
                    Add Printable
                </button>
            </div>

            {/* Toolbar & Filters */}
            <div className="manager-toolbar">
                <div className="search-box">
                    <Search size={18} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search printables by title..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <div className="toolbar-filters">
                    <select value={filterGradeId} onChange={(e) => setFilterGradeId(e.target.value)}>
                        <option value="ALL">All Grades</option>
                        {metaOptions.grades.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                        ))}
                    </select>

                    <select value={filterSubjectId} onChange={(e) => setFilterSubjectId(e.target.value)}>
                        <option value="ALL">All Subjects</option>
                        {metaOptions.subjects.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Table Data List */}
            {loading ? (
                <div className="manager-loading">Loading printables data...</div>
            ) : (
                <div className="table-wrapper">
                    <table className="printables-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Resource Title</th>
                                <th>Grade</th>
                                <th>Subject & Topic</th>
                                <th>Type</th>
                                <th>Downloads</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPrintables.map((item) => (
                                <tr key={item.id}>
                                    <td>#{item.id}</td>
                                    <td>
                                        <div className="title-cell">
                                            <FileText size={18} className="doc-icon" />
                                            <div>
                                                <div className="printable-name">{item.title}</div>
                                                {item.description && <div className="printable-desc">{item.description}</div>}
                                            </div>
                                        </div>
                                    </td>
                                    <td><span className="chip chip-grade">{item.grade?.name || 'All'}</span></td>
                                    <td>
                                        <div className="subject-topic-stack">
                                            <span className="chip chip-subject">{item.subject?.name || 'Unassigned'}</span>
                                            {item.topic?.name && <span className="chip chip-topic">{item.topic.name}</span>}
                                        </div>
                                    </td>
                                    <td>
                                        {item.pdfUrl ? (
                                            <span className="type-badge pdf-badge">Static PDF</span>
                                        ) : (
                                            <span className="type-badge dynamic-badge">Dynamic SVG</span>
                                        )}
                                    </td>
                                    <td><strong>{item.downloadCount}</strong></td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div className="table-actions">
                                            {item.pdfUrl && (
                                                <a
                                                    href={item.pdfUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="icon-action-btn view-btn"
                                                    title="Preview File"
                                                >
                                                    <ExternalLink size={16} />
                                                </a>
                                            )}
                                            <button
                                                className="icon-action-btn edit-btn"
                                                onClick={() => handleOpenModal(item)}
                                                title="Edit Printable"
                                            >
                                                <Edit3 size={16} />
                                            </button>
                                            <button
                                                className="icon-action-btn delete-btn"
                                                onClick={() => handleDelete(item.id)}
                                                title="Delete Printable"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredPrintables.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="empty-table-state">
                                        No printables found matching your current filter choices.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add / Edit Modal */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-card">
                        <div className="modal-header">
                            <h3>{editingItem ? `Edit Printable #${editingItem.id}` : 'Add New Printable'}</h3>
                            <button className="modal-close" onClick={() => setIsModalOpen(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Resource Title *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="e.g. Multiplication Tables 1 - 10"
                                />
                            </div>

                            <div className="form-grid-3">
                                <div className="form-group">
                                    <label>Grade</label>
                                    <select
                                        value={formData.gradeId}
                                        onChange={(e) => setFormData({ ...formData, gradeId: e.target.value, subjectId: '', topicId: ''  })}
                                    >
                                        <option value="">-- Select Grade --</option>
                                        {metaOptions.grades.map((g) => (
                                            <option key={g.id} value={g.id}>{g.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Subject</label>
                                    <select
                                        value={formData.subjectId}
                                        disabled={!formData.gradeId && formSubjects.length === 0}
                                        onChange={(e) => setFormData({ ...formData, subjectId: e.target.value, topicId: '' })}
                                    >
                                        <option value="">-- Select Subject --</option>
                                        {formSubjects.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    rows="2"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief summary describing this worksheet..."
                                />
                            </div>

                            <div className="form-section-title">File / Template Specification</div>

                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label>PDF Download URL (S3 / CDN link)</label>
                                    <input
                                        type="url"
                                        value={formData.pdfUrl}
                                        onChange={(e) => setFormData({ ...formData, pdfUrl: e.target.value })}
                                        placeholder="https://cdn.knolup.com/printables/sheet.pdf"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Thumbnail URL</label>
                                    <input
                                        type="url"
                                        value={formData.thumbnailUrl}
                                        onChange={(e) => setFormData({ ...formData, thumbnailUrl: e.target.value })}
                                        placeholder="https://cdn.knolup.com/printables/sheet_thumbnail.jpg"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <div className="label-with-action">
                                    <label>Metadata JSON (For Dynamic Templates)</label>
                                    <button type="button" className="prettify-btn" onClick={handlePrettifyJson}>
                                        <Code size={14} /> Format JSON
                                    </button>
                                </div>
                                <textarea
                                    rows="3"
                                    className="code-textarea"
                                    value={formData.metadataString}
                                    onChange={(e) => setFormData({ ...formData, metadataString: e.target.value })}
                                    placeholder='{"start": 1, "end": 10, "badge": "1 - 10"}'
                                />
                                {jsonError && (
                                    <div className="json-error-msg">
                                        <AlertCircle size={14} /> {jsonError}
                                    </div>
                                )}
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="save-btn" disabled={submitting}>
                                    {submitting ? 'Saving...' : editingItem ? 'Update Printable' : 'Create Printable'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PrintableManager;